import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { LayoutTemplate, Image as ImageIcon, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { get } from "@/lib/api-client";
import { storefrontApi } from "@/lib/extensions-api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

// 🛠️ FIXED: Removed title, subtitle, and target_url
interface SliderForm { item_type: string; display_order: number; }
interface SectionForm { title: string; section_type: string; banner_url: string; display_order: number; }

export function StorefrontAdminPage() {
  const qc = useQueryClient();
  const [sliderOpen, setSliderOpen] = useState<any>(null);
  const [sectionOpen, setSectionOpen] = useState<any>(null);
  
  const [deleteSliderTarget, setDeleteSliderTarget] = useState<number | null>(null);
  const [deleteSectionTarget, setDeleteSectionTarget] = useState<number | null>(null);
  
  const sliders = useQuery({ queryKey: ["admin-sliders"], queryFn: storefrontApi.admin.sliders });
  const sections = useQuery({ queryKey: ["admin-sections"], queryFn: storefrontApi.admin.sections });
  const categories = useQuery({ queryKey: ["admin-categories"], queryFn: () => get<any[]>("/catalog/categories") });
  const products = useQuery({ queryKey: ["admin-products"], queryFn: () => get<any[]>("/catalog/products") });

  const { register: regSlider, handleSubmit: handleSlider, reset: resetSlider } = useForm<SliderForm>();
  const { register: regSection, handleSubmit: handleSection, reset: resetSection, watch: watchSection } = useForm<SectionForm>();

  const [selectedCats, setSelectedCats] = useState<number[]>([]);
  const [selectedProds, setSelectedProds] = useState<number[]>([]);

  useEffect(() => {
    if (sliderOpen === "new") {
      // 🛠️ FIXED: Reset only the remaining fields
      resetSlider({ item_type: "custom_offer", display_order: 0 });
    } else if (sliderOpen) {
      resetSlider({
        item_type: sliderOpen.item_type || "custom_offer",
        display_order: sliderOpen.display_order || 0,
      });
    }
  }, [sliderOpen, resetSlider]);

  useEffect(() => {
    if (sectionOpen === "new") {
      resetSection({ section_type: "category_group", title: "", banner_url: "", display_order: 0 });
      setSelectedCats([]); setSelectedProds([]);
    } else if (sectionOpen) {
      resetSection({
        section_type: sectionOpen.section_type || "category_group", title: sectionOpen.title || "",
        banner_url: sectionOpen.banner_url || "", display_order: sectionOpen.display_order || 0,
      });
      setSelectedCats(sectionOpen.categories || []); setSelectedProds(sectionOpen.products || []);
    }
  }, [sectionOpen, resetSection]);

  const saveSlider = useMutation({
    mutationFn: (data: SliderForm) => {
      const formData = new FormData();
      formData.append("item_type", data.item_type); 
      formData.append("display_order", String(data.display_order));
      const fileInput = document.getElementById("slider-img") as HTMLInputElement;
      if (fileInput?.files?.[0]) formData.append("image", fileInput.files[0]);

      return sliderOpen === "new" ? storefrontApi.admin.createSlider(formData) : storefrontApi.admin.updateSlider(sliderOpen.id, formData);
    },
    onSuccess: () => {
      toast.success("Slider saved successfully!");
      qc.invalidateQueries({ queryKey: ["admin-sliders"] });
      setSliderOpen(null);
    }
  });

  const saveSection = useMutation({
    mutationFn: (data: SectionForm) => {
      const formData = new FormData();
      formData.append("title", data.title); formData.append("section_type", data.section_type);
      formData.append("banner_url", data.banner_url); formData.append("display_order", String(data.display_order));
      formData.append("categories", JSON.stringify(selectedCats)); formData.append("products", JSON.stringify(selectedProds));
      const fileInput = document.getElementById("section-img") as HTMLInputElement;
      if (fileInput?.files?.[0]) formData.append("banner_image", fileInput.files[0]);

      return sectionOpen === "new" ? storefrontApi.admin.createSection(formData) : storefrontApi.admin.updateSection(sectionOpen.id, formData);
    },
    onSuccess: () => {
      toast.success("Section saved successfully!");
      qc.invalidateQueries({ queryKey: ["admin-sections"] });
      setSectionOpen(null);
    }
  });

  const deleteSlider = useMutation({
    mutationFn: (id: number) => storefrontApi.admin.deleteSlider(id),
    onSuccess: () => { toast.success("Slider deleted!"); qc.invalidateQueries({ queryKey: ["admin-sliders"] }); setDeleteSliderTarget(null); }
  });

  const deleteSection = useMutation({
    mutationFn: (id: number) => storefrontApi.admin.deleteSection(id),
    onSuccess: () => { toast.success("Section deleted!"); qc.invalidateQueries({ queryKey: ["admin-sections"] }); setDeleteSectionTarget(null); }
  });

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10">
      <header><h1 className="text-2xl font-bold text-surface-900">Storefront CMS</h1></header>

      {/* Hero Sliders */}
      <Card>
        <CardHeader className="flex justify-between items-center border-b border-surface-100 bg-surface-50/50">
          <CardTitle><ImageIcon className="w-5 h-5 text-brand-600"/> Hero Sliders</CardTitle>
          <Button size="sm" leftIcon={<Plus className="w-4 h-4"/>} onClick={() => setSliderOpen("new")}>Add Slider</Button>
        </CardHeader>
        <CardBody className="p-0">
          {sliders.isLoading ? <Skeleton className="h-24 m-4" /> : (
            <Table>
              {/* 🛠️ FIXED: Removed Title and Target URL columns */}
              <THead><TR><TH>Image</TH><TH className="text-center">Order</TH><TH>Status</TH><TH className="text-right">Actions</TH></TR></THead>
              <tbody>
                {sliders.data?.map(b => (
                  <TR key={b.id} className="hover:bg-surface-50">
                    <TD>{b.image ? <img src={b.image} className="h-10 w-16 object-cover rounded-md border border-surface-200" /> : <span className="text-xs text-surface-400">No Image</span>}</TD>
                    <TD className="text-center font-semibold">{b.display_order}</TD>
                    <TD><StatusPill status={b.is_active ? "active" : "inactive"} /></TD>
                    <TD className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setSliderOpen(b)}><Edit className="h-4 w-4 text-surface-500 hover:text-brand-600"/></Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteSliderTarget(b.id)}><Trash2 className="h-4 w-4 text-danger-500 hover:text-danger-600"/></Button>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Homepage Sections */}
      <Card>
        <CardHeader className="flex justify-between items-center border-b border-surface-100 bg-surface-50/50">
          <CardTitle><LayoutTemplate className="w-5 h-5 text-brand-600"/> Homepage Sections</CardTitle>
          <Button size="sm" leftIcon={<Plus className="w-4 h-4"/>} onClick={() => setSectionOpen("new")}>Add Section</Button>
        </CardHeader>
        <CardBody className="p-0">
          {sections.isLoading ? <Skeleton className="h-24 m-4" /> : (
            <Table>
              <THead><TR><TH>Section Title</TH><TH>Type</TH><TH>Items Attached</TH><TH className="text-center">Order</TH><TH>Status</TH><TH className="text-right">Actions</TH></TR></THead>
              <tbody>
                {sections.data?.map(s => (
                  <TR key={s.id} className="hover:bg-surface-50">
                    <TD className="font-bold text-surface-900">{s.title}</TD>
                    <TD className="capitalize text-sm text-surface-600">{s.section_type.replace(/_/g, " ")}</TD>
                    <TD className="text-sm font-semibold text-brand-600">{s.section_type === "category_group" ? `${s.categories?.length || 0} Categories` : s.section_type === "product_group" ? `${s.products?.length || 0} Products` : String(s.section_type) === "category_only" ? `${s.categories?.length || 0} Icon Boxes` : "Banner Only"}</TD>
                    <TD className="text-center font-semibold">{s.display_order}</TD>
                    <TD><StatusPill status={s.is_active ? "active" : "inactive"} /></TD>
                    <TD className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setSectionOpen(s)}><Edit className="h-4 w-4 text-surface-500 hover:text-brand-600"/></Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteSectionTarget(s.id)}><Trash2 className="h-4 w-4 text-danger-500 hover:text-danger-600"/></Button>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Slider Modal */}
      <Modal open={!!sliderOpen} onClose={() => setSliderOpen(null)} title={sliderOpen === "new" ? "Add Hero Slider" : "Edit Hero Slider"} size="lg">
        <form onSubmit={handleSlider((data) => saveSlider.mutate(data))} className="space-y-4">
          <Select label="Item Type" options={[{value:"custom_offer", label:"Custom Banner"}, {value:"vendor_shop", label:"Vendor Shop"}]} {...regSlider("item_type")} />
          
          {/* 🛠️ FIXED: Removed Title, Subtitle, and Target URL inputs */}
          
          <div>
            <label className="block text-sm font-medium text-surface-800 mb-1.5">Background Image</label>
            <input type="file" id="slider-img" accept="image/*" className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:bg-brand-50" />
          </div>
          <Input label="Display Order" type="number" {...regSlider("display_order")} />
          <div className="flex justify-end pt-4 border-t"><Button type="submit" loading={saveSlider.isPending}>Save Slider</Button></div>
        </form>
      </Modal>

      {/* Section Modal */}
      <Modal open={!!sectionOpen} onClose={() => setSectionOpen(null)} title={sectionOpen === "new" ? "Add Homepage Section" : "Edit Section"} size="lg">
        <form onSubmit={handleSection((data) => saveSection.mutate(data))} className="space-y-4">
          <Input label="Section Title" {...regSection("title", { required: true })} />
          
          <Select 
            label="Section Type" 
            options={[
              {value:"category_group", label:"Category Wise Products"}, 
              {value:"product_group",  label:"Specific Products"}, 
              {value:"banner",         label:"Image Banner"},
              {value:"category_only",  label:"Only Categories (Icon Grid)"} 
            ]} 
            {...regSection("section_type")} 
          />

          {(watchSection("section_type") === "category_group" || watchSection("section_type") === "category_only") && (
            <div className="bg-surface-50 p-4 rounded-xl border">
              <label className="block text-sm font-bold mb-2">Select Categories</label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {categories.data?.map(c => {
                  const isSelected = selectedCats.includes(c.id);
                  return <button key={c.id} type="button" onClick={() => setSelectedCats(prev => isSelected ? prev.filter(id => id !== c.id) : [...prev, c.id])} className={`px-3 py-1.5 text-xs rounded-lg border ${isSelected ? 'bg-brand-600 text-white' : 'bg-white'}`}>{c.name}</button>;
                })}
              </div>
            </div>
          )}

          {watchSection("section_type") === "product_group" && (
            <div className="bg-surface-50 p-4 rounded-xl border">
              <label className="block text-sm font-bold mb-2">Select Products</label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {products.data?.map(p => {
                  const isSelected = selectedProds.includes(p.id);
                  return <button key={p.id} type="button" onClick={() => setSelectedProds(prev => isSelected ? prev.filter(id => id !== p.id) : [...prev, p.id])} className={`px-3 py-1.5 text-xs rounded-lg border ${isSelected ? 'bg-brand-600 text-white' : 'bg-white'}`}><div className="truncate w-32">{p.name}</div></button>;
                })}
              </div>
            </div>
          )}

          {watchSection("section_type") === "banner" && (
            <div className="bg-surface-50 p-4 rounded-xl border space-y-3">
              <Input label="Banner Target URL" {...regSection("banner_url")} />
              <div>
                <label className="block text-sm font-medium mb-1.5">Banner Image</label>
                <input type="file" id="section-img" accept="image/*" className="w-full text-sm file:bg-brand-50 file:rounded-lg file:px-4 file:py-2" />
              </div>
            </div>
          )}

          <Input label="Display Order" type="number" {...regSection("display_order")} />
          
          <div className="flex justify-end pt-4 border-t"><Button type="submit" loading={saveSection.isPending}>Save Section</Button></div>
        </form>
      </Modal>

      {/* Delete Dialogs */}
      <ConfirmDialog open={!!deleteSliderTarget} onClose={() => setDeleteSliderTarget(null)} title="Delete Slider?" message="Are you sure you want to delete this slider permanently?" confirmLabel="Delete" destructive loading={deleteSlider.isPending} onConfirm={() => deleteSlider.mutate(deleteSliderTarget!)} />
      <ConfirmDialog open={!!deleteSectionTarget} onClose={() => setDeleteSectionTarget(null)} title="Delete Section?" message="Are you sure you want to delete this section permanently?" confirmLabel="Delete" destructive loading={deleteSection.isPending} onConfirm={() => deleteSection.mutate(deleteSectionTarget!)} />
    </div>
  );
}